export type RenderMode = 'liquid' | 'image' | 'video' | 'ripple' | 'liquid-v1' | 'shader';

// +++ NEW: Define a type for our richer ripple data
type RipplePoint = {
  x: number;
  y: number;
  startTime: number;
  intensity: number;
  color: [number, number, number]; // r, g, b
};

export class Renderer {
    private canvas: HTMLCanvasElement;
    private device!: GPUDevice;
    private context!: GPUCanvasContext;
    private presentationFormat!: GPUTextureFormat;
    private pipelines = new Map<string, GPURenderPipeline | GPUComputePipeline>();
    private bindGroups = new Map<string, GPUBindGroup>();
    private sampler!: GPUSampler;
    private imageUrls: string[] = [];
    private MAX_RIPPLES = 50;
    private v2ComputeUniformBuffer!: GPUBuffer;
    private v1ComputeUniformBuffer!: GPUBuffer;
    private imageVideoUniformBuffer!: GPUBuffer;
    private galaxyUniformBuffer!: GPUBuffer;
    private videoTexture!: GPUTexture;
    private imageTexture!: GPUTexture;
    private writeTexture!: GPUTexture;
  
    constructor(canvas: HTMLCanvasElement) {
      this.canvas = canvas; 
    }
  
    private ripplePoints: RipplePoint[] = [];
    // +++ MODIFIED: This method now creates a full RipplePoint object
    public addRipplePoint(x: number, y: number) {
    this.ripplePoints.push({
      x,
      y,
      startTime: performance.now() / 1000.0,
      intensity: 0.9, // A default intensity
      color: [Math.random(), Math.random(), Math.random()], // A random color for fun!
    });
    }

    public async init(): Promise<boolean> {
        if (!navigator.gpu) return false;
        const adapter = await navigator.gpu.requestAdapter();
        if (!adapter) return false;
        this.device = await adapter.requestDevice();
        this.context = this.canvas.getContext('webgpu')!;
        this.presentationFormat = navigator.gpu.getPreferredCanvasFormat();
        this.context.configure({ device: this.device, format: this.presentationFormat, alphaMode: 'premultiplied' });
        await this.fetchImageUrls();
        await this.createResources();
        await this.createPipelines();
        this.createBindGroups();
        return true;
    }

    private async fetchImageUrls(): Promise<void> {
        const bucketName = 'my-sd35-space-images-2025';
        const apiUrl = `https://storage.googleapis.com/storage/v1/b/${bucketName}/o`;
        try {
            const response = await fetch(apiUrl);
            if (!response.ok) throw new Error(`API error: ${response.status}`);
            const data = await response.json();
            this.imageUrls = data.items ? data.items.map((item: { name: string }) => `https://storage.googleapis.com/${bucketName}/${item.name}`) : [];
        } catch (e) {
            console.error("Failed to fetch image list:", e);
            this.imageUrls = ['https://i.imgur.com/vCNL2sT.jpeg'];
        }
    }

    public async loadRandomImage(): Promise<void> {
        try {
            if (this.imageUrls.length === 0) return;
            const imageUrl = this.imageUrls[Math.floor(Math.random() * this.imageUrls.length)];
            const response = await fetch(imageUrl);
            const imageBitmap = await createImageBitmap(await response.blob());
            if (this.imageTexture) this.imageTexture.destroy();
            this.imageTexture = this.device.createTexture({
                size: [imageBitmap.width, imageBitmap.height],
                format: 'rgba16float',
                usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_SRC,
            });
            this.device.queue.copyExternalImageToTexture({ source: imageBitmap }, { texture: this.imageTexture }, [imageBitmap.width, imageBitmap.height]);
            if (this.pipelines.size > 0) {
                this.createBindGroups();
            }
        } catch (e) { console.error("Failed to load image:", e); }
    }

    private async createResources(): Promise<void> {
        const { width, height } = this.canvas;
        this.sampler = this.device.createSampler({ magFilter: 'linear', minFilter: 'linear' });
        this.galaxyUniformBuffer = this.device.createBuffer({ size: 16, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
        this.imageVideoUniformBuffer = this.device.createBuffer({ size: 32 + (this.MAX_RIPPLES * 16), usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
        this.v1ComputeUniformBuffer = this.device.createBuffer({ size: 16, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
        const v2UniformBufferSize = 16 + (this.MAX_RIPPLES * 32); // 16 for config, 32 per ripple
        this.v2ComputeUniformBuffer = this.device.createBuffer({
          size: v2UniformBufferSize,
          usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
        });
  this.writeTexture = this.device.createTexture({
      size: [width, height],
      format: 'rgba16float', // <-- The key change for HDR!
      usage: GPUTextureUsage.COPY_DST | GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
    });
        await this.loadRandomImage();
    }

  private async createPipelines(): Promise<void> {
    const [galaxyCode, imageVideoCode, liquidV1Code, liquidCode, textureCode, tonemapCode] = await Promise.all([
      fetch('shaders/galaxy.wgsl').then(res => res.text()),
      fetch('shaders/imageVideo.wgsl').then(res => res.text()),
      fetch('shaders/liquid-v1.wgsl').then(res => res.text()),
      fetch('shaders/liquid.wgsl').then(res => res.text()),
      fetch('shaders/texture.wgsl').then(res => res.text()),
      fetch('shaders/tonemap.wgsl').then(res => res.text()), // <-- ADDED
    ]);
        const galaxyModule = this.device.createShaderModule({ code: galaxyCode });
        const imageVideoModule = this.device.createShaderModule({ code: imageVideoCode });
        const liquidV1Module = this.device.createShaderModule({ code: liquidV1Code });
        const liquidModule = this.device.createShaderModule({ code: liquidCode });
        const textureModule = this.device.createShaderModule({ code: textureCode });
        const tonemapModule = this.device.createShaderModule({ code: tonemapCode }); // <-- ADDED
        const commonConfig = { vertex: { module: imageVideoModule, entryPoint: 'vs_main' }, fragment: { targets: [{ format: this.presentationFormat }] }, primitive: { topology: 'triangle-strip' as GPUPrimitiveTopology } };
        this.pipelines.set('galaxy', this.device.createRenderPipeline({ layout: 'auto', ...commonConfig, vertex: { module: galaxyModule, entryPoint: 'vs_main' }, fragment: { ...commonConfig.fragment, module: galaxyModule, entryPoint: 'fs_main' }, primitive: { topology: 'triangle-list' as GPUPrimitiveTopology } }));
        this.pipelines.set('imageVideo', this.device.createRenderPipeline({ layout: 'auto', ...commonConfig, fragment: { ...commonConfig.fragment, module: imageVideoModule, entryPoint: 'fs_main' } }));
        this.pipelines.set('liquid', this.device.createRenderPipeline({ layout: 'auto', ...commonConfig, vertex: { module: textureModule, entryPoint: 'vs_main' }, fragment: { ...commonConfig.fragment, module: textureModule, entryPoint: 'fs_main' } }));
        this.pipelines.set('computeV1', this.device.createComputePipeline({ layout: 'auto', compute: { module: liquidV1Module, entryPoint: 'main' } }));
        this.pipelines.set('compute', this.device.createComputePipeline({ layout: 'auto', compute: { module: liquidModule, entryPoint: 'main' } }));
        this.pipelines.set('tonemap', this.device.createRenderPipeline({
            layout: 'auto',
            vertex: {
              module: tonemapModule,
              entryPoint: 'vs_main',
            },
            fragment: {
              module: tonemapModule,
              entryPoint: 'fs_main',
              targets: [{ format: this.presentationFormat }],
            },
            primitive: {
              topology: 'triangle-strip',
            },
          }));
    }

    private createBindGroups(): void {
        if (!this.imageTexture) return;
        if (this.videoTexture) {
            this.bindGroups.set('galaxy', this.device.createBindGroup({ layout: this.pipelines.get('galaxy')!.getBindGroupLayout(0), entries: [{ binding: 0, resource: { buffer: this.galaxyUniformBuffer } }, { binding: 1, resource: this.sampler }, { binding: 2, resource: this.videoTexture.createView() }] }));
            this.bindGroups.set('video', this.device.createBindGroup({ layout: this.pipelines.get('imageVideo')!.getBindGroupLayout(0), entries: [{ binding: 0, resource: this.sampler }, { binding: 1, resource: this.videoTexture.createView() }, { binding: 2, resource: { buffer: this.imageVideoUniformBuffer } }] }));
        }
        this.bindGroups.set('image', this.device.createBindGroup({ layout: this.pipelines.get('imageVideo')!.getBindGroupLayout(0), entries: [{ binding: 0, resource: this.sampler }, { binding: 1, resource: this.imageTexture.createView() }, { binding: 2, resource: { buffer: this.imageVideoUniformBuffer } }] }));
        this.bindGroups.set('liquid', this.device.createBindGroup({ layout: this.pipelines.get('liquid')!.getBindGroupLayout(0), entries: [{ binding: 0, resource: this.sampler }, { binding: 1, resource: this.writeTexture.createView() }] }));
        this.bindGroups.set('computeV1', this.device.createBindGroup({ 
            layout: this.pipelines.get('computeV1')!.getBindGroupLayout(0), 
            entries: [
                { binding: 0, resource: this.sampler }, 
                { binding: 1, resource: this.imageTexture.createView() }, 
                { binding: 2, resource: this.writeTexture.createView() },
                { binding: 3, resource: { buffer: this.v1ComputeUniformBuffer } } // This line was missing.
            ] 
        }));
        this.bindGroups.set('compute', this.device.createBindGroup({ 
            layout: this.pipelines.get('compute')!.getBindGroupLayout(0), 
            entries: [
                { binding: 0, resource: this.sampler }, 
                { binding: 1, resource: this.imageTexture.createView() }, 
                { binding: 2, resource: this.writeTexture.createView() }, 
                { binding: 3, resource: { buffer: this.v2ComputeUniformBuffer } }
            ] 
        }));
        this.bindGroups.set('tonemap', this.device.createBindGroup({
          layout: this.pipelines.get('tonemap')!.getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: this.sampler },
            { binding: 1, resource: this.writeTexture.createView() }
          ]
        }));
    }

    public render(mode: RenderMode, videoElement: HTMLVideoElement, zoom: number, panX: number, panY: number): void {
        if (!this.device || !this.imageTexture) return;
        const currentTime = performance.now() / 1000.0;
        if (videoElement.readyState >= 2 && videoElement.videoWidth > 0) {
            if (!this.videoTexture || this.videoTexture.width !== videoElement.videoWidth || this.videoTexture.height !== videoElement.videoHeight) {
                if (this.videoTexture) this.videoTexture.destroy();
                this.videoTexture = this.device.createTexture({ size: [videoElement.videoWidth, videoElement.videoHeight], format: 'rgba16float', usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT });
                this.createBindGroups();
            }
            this.device.queue.copyExternalImageToTexture({ source: videoElement }, { texture: this.videoTexture }, [videoElement.videoWidth, videoElement.videoHeight]);
        }
        const commandEncoder = this.device.createCommandEncoder();
        if (mode.startsWith('liquid')) {
            const computePass = commandEncoder.beginComputePass();
            const computeV1BG = this.bindGroups.get('computeV1');
            const computeBG = this.bindGroups.get('compute');
                if (mode === 'liquid' && computeBG) {
                // Filter out old ripples
                this.ripplePoints = this.ripplePoints.filter(p => (currentTime - p.startTime) < 4.0);
                // Ensure we don't exceed the buffer limit
                if (this.ripplePoints.length > this.MAX_RIPPLES) {
                    this.ripplePoints.splice(0, this.ripplePoints.length - this.MAX_RIPPLES);
                }
                // Each ripple struct is 8 floats long (vec2 + f32 + f32 + vec3 + padding)
                const floatsPerRipple = 8;
                const configFloatCount = 4;
                const totalFloatCount = configFloatCount + this.MAX_RIPPLES * floatsPerRipple;
                const computeUniformArray = new Float32Array(totalFloatCount);
                // 1. Set config data (time, rippleCount, resolutionX, resolutionY)
                computeUniformArray.set([currentTime, this.ripplePoints.length, this.canvas.width, this.canvas.height], 0);
                // 2. Loop through our ripples and serialize them into the array
                for (let i = 0; i < this.ripplePoints.length; i++) {
                  const point = this.ripplePoints[i];
                  // This is the data for a single ripple struct, flattened into an array
                  const rippleStructData = [
                    point.x,              // center.x
                    point.y,              // center.y
                    point.startTime,      // startTime
                    point.intensity,      // intensity
                    point.color[0],       // color.r
                    point.color[1],       // color.g
                    point.color[2],       // color.b
                    0.0                   // CRITICAL: Explicit padding to match 32-byte alignment
                  ];
                  // Calculate the starting position (in floats) for this ripple in the main array
                  const offset = configFloatCount + (i * floatsPerRipple);
                  computeUniformArray.set(rippleStructData, offset);
                }
                // 3. Write the entire array to the GPU buffer
                this.device.queue.writeBuffer(this.v2ComputeUniformBuffer, 0, computeUniformArray);
                // Dispatch the compute shader (this part is unchanged)
                computePass.setPipeline(this.pipelines.get('compute') as GPUComputePipeline);
                computePass.setBindGroup(0, computeBG);
                computePass.dispatchWorkgroups(Math.ceil(this.canvas.width / 8), Math.ceil(this.canvas.height / 8), 1);
              }
              computePass.end();
            }
            const textureView = this.context.getCurrentTexture().createView();
            const renderPassDescriptor: GPURenderPassDescriptor = { colorAttachments: [{ view: textureView, clearValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 }, loadOp: 'clear' as GPULoadOp, storeOp: 'store' as GPUStoreOp }] };
            const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
            const liquidPipeline = this.pipelines.get('liquid') as GPURenderPipeline;
            const imageVideoPipeline = this.pipelines.get('imageVideo') as GPURenderPipeline;
            const galaxyPipeline = this.pipelines.get('galaxy') as GPURenderPipeline;
            switch (mode) {
                case 'shader':
                    if (galaxyPipeline && this.bindGroups.has('galaxy')) {
                        this.device.queue.writeBuffer(this.galaxyUniformBuffer, 0, new Float32Array([currentTime, zoom, panX, panY]));
                        passEncoder.setPipeline(galaxyPipeline);
                        passEncoder.setBindGroup(0, this.bindGroups.get('galaxy')!);
                        passEncoder.draw(6);
                    }
                    break;
                case 'image':
                case 'ripple':
                    if (imageVideoPipeline && this.bindGroups.has('image')) {
                        // --- FIX #2: Rewrote uniform buffer creation to be safer ---
                        const uniformArray = new Float32Array(8 + this.MAX_RIPPLES * 4);
                        uniformArray.set([this.canvas.width, this.canvas.height, this.imageTexture.width, this.imageTexture.height], 0);
                        uniformArray.set([currentTime, this.ripplePoints.length, mode === 'ripple' ? 1.0 : 0.0, 0.0], 4);
                        for (let i = 0; i < this.ripplePoints.length; i++) {
                            const point = this.ripplePoints[i];
                            uniformArray.set([point.x, point.y, point.startTime, 0.0], 8 + i * 4);
                        }
                        this.device.queue.writeBuffer(this.imageVideoUniformBuffer, 0, uniformArray);
                        passEncoder.setPipeline(imageVideoPipeline);
                        passEncoder.setBindGroup(0, this.bindGroups.get('image')!);
                        passEncoder.draw(4);
                    }
                    break;
                case 'video':
                    if (imageVideoPipeline && this.bindGroups.has('video')) {
                        const uniformArray = new Float32Array(8);
                        uniformArray.set([this.canvas.width, this.canvas.height, this.videoTexture.width, this.videoTexture.height], 0);
                        uniformArray.set([currentTime, 0, 0, 0], 4);
                        this.device.queue.writeBuffer(this.imageVideoUniformBuffer, 0, uniformArray);
                        passEncoder.setPipeline(imageVideoPipeline);
                        passEncoder.setBindGroup(0, this.bindGroups.get('video')!);
                        passEncoder.draw(4);
                    }
                    break;
                case 'liquid-v1':
                case 'liquid':
                // --- MODIFIED: Use the new tonemap pipeline for the final render
                const tonemapPipeline = this.pipelines.get('tonemap') as GPURenderPipeline;
                if (tonemapPipeline && this.bindGroups.has('tonemap')) {
                  passEncoder.setPipeline(tonemapPipeline);
                  passEncoder.setBindGroup(0, this.bindGroups.get('tonemap')!);
                  passEncoder.draw(4);
                }
                break;
            }
            passEncoder.end();
            this.device.queue.submit([commandEncoder.finish()]);
        }
}
